import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect, withRouter } from 'react-router-dom';
import { SiteNav, SiteNavMobile } from 'msp-components';
import Error404 from 'components/Error404';
import Wrapper from 'components/Wrapper';
import Title from 'components/Title';
import templateOptionsPropTypes from 'components/templateOptionsPropTypes';

const WebsiteWrapper = ({ project, menuItems, templateOptions, sections, items, history, location }) => {
  const [containerSize, setContainerSize] = useState({});
  const handleResize = (width, height) => setContainerSize({ width, height });

  const navItems = useMemo(() => (
    sections.ordered.reduce((result, sId) => {
      const section = sections.byId[sId];
      const subItems = section.items.filter(item => item.inSectionNav).map(item => ({
        id: item.uid,
        label: item.linkText,
      }));
      result.push({
        id: section.uid,
        label: section.name,
        subItems,
      });
      return result;
    }, [])
  ), [sections]);

  const titlePaths = useMemo(() => (
    sections.ordered.reduce((result, sId) => {
      const section = sections.byId[sId];
      section.items.forEach((item, idx) => {
        if (idx === 0) {
          // First item in section
          result[item.uid] = `/${section.slug}`;
        } else {
          result[item.uid] = `/${section.slug}/${item.urlSlug}`;
        }
      });
      return result;
    }, {})
  ), [sections]);

  const { itemsOrdered, itemsById } = sections.ordered.reduce((result, sId) => {
    const section = sections.byId[sId];
    section.items.forEach(item => {
      result.itemsOrdered.push(item.uid);
      result.itemsById[item.uid] = item;
    });
    return result;
  }, { itemsOrdered: [], itemsById: {} });

  const firstItemPath = titlePaths[itemsOrdered[0]];

  // Sort paths by complexity, so that routes appear in proper priority order
  const parts = path => path.split('/').filter(s => !!s).length;
  const routePaths = Object.entries(titlePaths).sort((a, b) => parts(b[1]) - parts(a[1]));

  const activeRoute = routePaths.find(([, path]) => {
    const pathToMatch = location.pathname === '/' ? firstItemPath : location.pathname;
    return pathToMatch === path || pathToMatch.startsWith(path + '/');
  });
  const activeItemId = activeRoute && activeRoute[0];
  const activeItem = !!activeItemId && items.byId[activeItemId];
  const showMainNav = !!activeItem && activeItem.showMainNav;
  const showSubnav = !!activeItem && activeItem.showSectionNav;

  let activeNavItemId = activeItemId;
  if (!activeItem.inSectionNav) {
    activeNavItemId = Object.entries(items.bySection).reduce((result, [sectionId, subItems]) => {
      if (subItems.includes(activeItemId)) result = sectionId;
      return result;
    }, null);
  }

  const handleItemSelect = id => {
    // If a section is selected, navigate to the first item within that section
    const itemId = id in items.bySection ? items.bySection[id][0] : id;
    history.push(titlePaths[itemId]);
  };

  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const handleMenuItemSelect = setActiveMenuItem;

  const routes = (
    <Route render={({ location }) => {
      // Remove trailing slash, preserving query params
      if (location.pathname.endsWith('/') && location.pathname !== '/') {
        return <Redirect to={location.pathname.slice(0, -1) + location.search} />;
      }

      // Check for global error state
      return location.state && location.state.is404 ? <Error404 /> : (
        <Switch>
          <Route
            exact
            path="/"
            render={props => {
              return (
                <Title
                  routeProps={props}
                  isHomepage
                  canonicalPath={firstItemPath}
                  containerWidth={containerSize.width}
                  project={project}
                  menuItems={menuItems}
                  data={itemsById[itemsOrdered[0]]}
                  onSelectMenuItem={handleMenuItemSelect}
                />
              );
            }}
          />
          {routePaths.map(([id, path]) => (
            <Route
              key={id}
              path={path}
              render={props => (
                <Title
                  routeProps={props}
                  containerWidth={containerSize.width}
                  project={project}
                  menuItems={menuItems}
                  data={items.byId[id]}
                  onSelectMenuItem={handleMenuItemSelect}
                />
              )}
            />
          ))}
          <Route component={Error404} />
        </Switch>
      );
    }} />
  );


  const NavComponent = containerSize.width < 650 || containerSize.height < 650 ? SiteNavMobile : SiteNav;
  const navigation = typeof containerSize.width !== 'undefined' && showMainNav && (
    <NavComponent
      items={navItems}
      activeItemId={activeNavItemId}
      showSubnav={showSubnav}
      onSelectItem={handleItemSelect}
    />
  );

  return (
    <Wrapper
      project={project}
      titlePaths={titlePaths}
      itemsOrdered={itemsOrdered}
      itemsById={itemsById}
      isHomepage={location.pathname === '/'}
      templateOptions={templateOptions}
      navigation={navigation}
      activeItem={activeItem}
      activeMenuItem={activeMenuItem}
      onContainerResize={handleResize}
    >
      {routes}
    </Wrapper>
  );
};

WebsiteWrapper.propTypes = {
  history: PropTypes.object,
  location: PropTypes.object,
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    content: PropTypes.string,
    isVisible: PropTypes.bool,
  })),
  templateOptions: PropTypes.shape(templateOptionsPropTypes),
  project: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    shortDescription: PropTypes.string,
    longDescription: PropTypes.string,
    socialImage: PropTypes.string,
    analyticsProvider: PropTypes.string,
    analyticsId: PropTypes.string,
    shareUrl: PropTypes.string,
    publisher: PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      twitter: PropTypes.string,
      facebook: PropTypes.string,
    }),
  }),
  sections: PropTypes.shape({
    byId: PropTypes.object,
    ordered: PropTypes.arrayOf(PropTypes.string),
  }),
  items: PropTypes.shape({
    byId: PropTypes.object,
    bySection: PropTypes.object,
  }),
};

export default withRouter(WebsiteWrapper);
